<template>
  <div :class="bannerClasses">
    <UIcon v-if="icon" :name="icon" class="size-6 shrink-0" />
    <AuxoIcon
      v-else
      :icon="VARIANT_ICON[variant]"
      size="lg"
      class="shrink-0"
      :cursor-pointer="false"
    />
    <div class="flex flex-1 flex-col gap-1.5">
      <p v-if="hasTitle" class="text-sm leading-4 font-bold">
        <slot name="title" />
      </p>
      <div class="text-sm leading-4">
        <slot>{{ message }}</slot>
      </div>
    </div>
    <button
      v-if="$slots.action"
      type="button"
      :class="actionClasses"
      @click="$emit('action')"
    >
      <slot name="action" />
    </button>
    <AuxoIcon
      v-if="hasCloseIcon"
      icon="feedback-close"
      size="md"
      as-button
      aria-label="Close"
      :class="['shrink-0', VARIANT_CLOSE_CLASS[variant]]"
      @click="$emit('close')"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import AuxoIcon from '@/components/auxo/AuxoIcon.vue';

const props = defineProps({
  variant: {
    type: String,
    default: 'info',
    validator: value =>
      ['info', 'success', 'warning', 'error'].includes(value),
  },
  layout: {
    type: String,
    default: 'long',
    validator: value => ['short', 'long'].includes(value),
  },
  // Iconify/lucide name (e.g. "i-lucide-sparkles"), rendered via UIcon.
  // Overrides the variant's default asset icon (rendered via AuxoIcon) — the two
  // icon systems use unrelated naming, so this is NOT an AuxoIcon asset filename.
  icon: {
    type: String,
    default: '',
  },
  hasTitle: {
    type: Boolean,
    default: false,
  },
  hasCloseIcon: {
    type: Boolean,
    default: false,
  },
  // Fallback body content for callers that can only pass props, not slots
  // (e.g. vue-sonner's toast.custom(), which renders custom components with no
  // slot passthrough). Ignored whenever a default slot is actually provided.
  message: {
    type: String,
    default: '',
  },
});

defineEmits(['action', 'close']);

const VARIANT_ICON = {
  info: 'feedback-info',
  success: 'feedback-success',
  warning: 'feedback-warning',
  error: 'feedback-error',
};

// The close icon is a single currentColor asset (feedback-close.svg) shared by
// all variants — AuxoIcon's own default button variant sets text-gray-600, so
// this needs `!` to win regardless of Tailwind's generated class order.
const VARIANT_CLOSE_CLASS = {
  info: 'text-information-700!',
  success: 'text-success-700!',
  warning: 'text-warning-700!',
  error: 'text-error-700!',
};

// Bg/border/text reference the color-foundations ramp from CPRD-5321 directly
// (--color-error-*/-information-*/-success-*/-warning-* in index.css's @theme static block).
const VARIANT_STYLES = {
  info: 'bg-information-25 text-information-700 border-information-100 outline-information-100',
  success:
    'bg-success-25 text-success-700 border-success-100 outline-success-100',
  warning:
    'bg-warning-50 text-warning-700 border-warning-100 outline-warning-100',
  error: 'bg-error-25 text-error-700 border-error-50 outline-error-50',
};

const LAYOUT_STYLES = {
  long: 'border-t border-b',
  short: 'rounded-xl shadow-md outline outline-1 outline-offset-[-1px]',
};

const actionClasses = 'shrink-0 rounded p-1 transition hover:bg-black/5';

const bannerClasses = computed(() => [
  'flex gap-4 p-4',
  // Top-align icon/close with a title+body block (icon is taller than one
  // line of text); center them against a single line of body-only content.
  props.hasTitle ? 'items-start' : 'items-center',
  LAYOUT_STYLES[props.layout],
  VARIANT_STYLES[props.variant],
]);
</script>
